import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";

export default function ControleUsuarioForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    loginName: "",
    usuarioWebId: "",
    horarioBloqueioInicio: "",
    horarioBloqueioFim: "",
    removerBloqueioHorario: true // Inicia como true (sem bloqueio por padrão)
  });
  const [bancos, setBancos] = useState([]);
  const [bancosDisponiveis, setBancosDisponiveis] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);

  // Carrega usuários web e bancos disponíveis
  useEffect(() => {
    // Busca todos os usuários web ativos
    const loadUsuarios = axios.get(`${process.env.REACT_APP_API}/usuario?ativo=true&pageSize=1000`)
      .then(res => {
        const data = res.data?.data?.items || res.data?.data || [];
        return Array.isArray(data) ? data : [];
      });

    // Busca todos os logins SQL para verificar quais usuários já estão associados
    const loadLoginsSQL = axios.get(`${process.env.REACT_APP_API}/controle-usuarios?ativo=true&pageSize=1000`)
      .then(res => {
        const data = res.data?.data?.items || res.data?.data || [];
        return Array.isArray(data) ? data : [];
      });

    Promise.all([loadUsuarios, loadLoginsSQL])
      .then(([todosUsuarios, todosLogins]) => {
        // IDs de usuários já associados a outros logins (exceto o atual se estiver editando)
        const usuariosAssociados = todosLogins
          .filter(login => login.usuarioWebId && (!isEdit || login.id !== parseInt(id)))
          .map(login => login.usuarioWebId);

        // Filtra apenas usuários disponíveis
        const usuariosDisponiveis = todosUsuarios.filter(
          usuario => !usuariosAssociados.includes(usuario.id)
        );

        setUsuarios(usuariosDisponiveis);
      })
      .catch(err => console.error(err));

    // Carrega bancos disponíveis
    axios.get(`${process.env.REACT_APP_API}/bancos?perPage=1000`)
      .then(res => {
        const items = res.data?.data?.items || [];
        const bancosData = items.map(item => item.Nome || item.nome);
        setBancosDisponiveis(bancosData);
      })
      .catch(err => {
        console.error('Erro ao carregar bancos:', err);
        AlertComponent("Aviso", "Não foi possível carregar a lista de bancos disponíveis", { icon: "warning" });
      });
  }, [id, isEdit]);

  // Carrega dados do controle usuario se for edição
  useEffect(() => {
    if (isEdit) {
      Promise.all([
        axios.get(`${process.env.REACT_APP_API}/controle-usuarios/${id}`),
        axios.get(`${process.env.REACT_APP_API}/controle-usuarios/${id}/bancos`)
      ])
        .then(([resControle, resBancos]) => {
          const data = resControle.data?.data || resControle.data;
          
          // Verifica se tem horários definidos
          const temHorarioInicio = !!data.horarioBloqueioInicio;
          const temHorarioFim = !!data.horarioBloqueioFim;
          const temBloqueio = temHorarioInicio || temHorarioFim;
          
          setFormData({
            loginName: data.loginName || "",
            usuarioWebId: data.usuarioWebId || "",
            horarioBloqueioInicio: data.horarioBloqueioInicio ? data.horarioBloqueioInicio.substring(0, 5) : "",
            horarioBloqueioFim: data.horarioBloqueioFim ? data.horarioBloqueioFim.substring(0, 5) : "",
            removerBloqueioHorario: !temBloqueio // Se não tem bloqueio, checkbox marcado
          });
          
          const bancosData = resBancos.data?.data || [];
          setBancos(Array.isArray(bancosData) ? bancosData : []);
        })
        .catch((err) => {
          console.error(err);
          AlertComponent("Erro", "Erro ao carregar dados", { icon: "error" });
        })
        .finally(() => setLoadingData(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Se marcou o checkbox de remover bloqueio, limpa os horários
    if (name === "removerBloqueioHorario" && checked) {
      setFormData((prev) => ({ 
        ...prev,
        removerBloqueioHorario: true,
        horarioBloqueioInicio: "",
        horarioBloqueioFim: ""
      }));
    } else {
      setFormData((prev) => ({ 
        ...prev, 
        [name]: type === "checkbox" ? checked : value 
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        usuarioWebId: formData.usuarioWebId ? parseInt(formData.usuarioWebId) : null,
        // Se checkbox marcado, envia null para remover os horários
        horarioBloqueioInicio: formData.removerBloqueioHorario ? null : formData.horarioBloqueioInicio,
        horarioBloqueioFim: formData.removerBloqueioHorario ? null : formData.horarioBloqueioFim
      };

      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API}/controle-usuarios/${id}`, payload);
        AlertComponent("Sucesso", "Login SQL salvo com sucesso!", { icon: "success" });
      } else {
        await axios.post(`${process.env.REACT_APP_API}/controle-usuarios`, payload);
        AlertComponent("Sucesso", "Login SQL criado com sucesso!", { icon: "success" });
      }
      navigate(`/controle-usuarios?filtro=${encodeURIComponent(formData.loginName)}`);
    } catch (error) {
      console.error("Erro ao salvar:", error);
      AlertComponent("Erro", error?.response?.data?.message ?? "Erro ao salvar", { icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Funções para gerenciar bancos
  const handleAdicionarBanco = async (banco, acesso) => {
    try {
      await axios.post(`${process.env.REACT_APP_API}/controle-usuarios/${id}/bancos`, {
        banco,
        acesso
      });
      
      const res = await axios.get(`${process.env.REACT_APP_API}/controle-usuarios/${id}/bancos`);
      const bancosData = res.data?.data || [];
      setBancos(Array.isArray(bancosData) ? bancosData : []);
      
      // Remove da lista de disponíveis
      setBancosDisponiveis(prev => prev.filter(b => b !== banco));
      
      AlertComponent("Sucesso", "Banco adicionado!", { icon: "success" });
    } catch (error) {
      AlertComponent("Erro", error?.response?.data?.message ?? "Erro ao adicionar banco", { icon: "error" });
    }
  };

  const handleRemoverBanco = async (bancoId) => {
  try {
    // Guarda o nome do banco antes de remover
    const bancoRemovido = bancos.find(b => b.id === bancoId);
    
    await axios.delete(`${process.env.REACT_APP_API}/controle-usuarios/bancos/${bancoId}`);
    
    // Remove da lista local
    setBancos(bancos.filter(b => b.id !== bancoId));
    
    // Adiciona de volta na lista de disponíveis (se não estiver lá)
    if (bancoRemovido && !bancosDisponiveis.includes(bancoRemovido.banco)) {
      setBancosDisponiveis(prev => [...prev, bancoRemovido.banco].sort());
    }
    
    AlertComponent("Sucesso", "Banco removido!", { icon: "success" });
  } catch (error) {
    AlertComponent("Erro", error?.response?.data?.message ?? "Erro ao remover banco", { icon: "error" });
  }
};

  const [novoBanco, setNovoBanco] = useState({ banco: "", acesso: "R" });

  if (loadingData) return <Loading />;

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Editar Login SQL" : "Novo Login SQL"}
        </h1>
        <p className="text-sm text-slate-500">
          {isEdit
            ? "Atualize as informações do login SQL Server"
            : "Preencha as informações para cadastrar um novo login SQL Server"}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white p-8 rounded-xl shadow border mb-6">
          <h2 className="text-lg font-medium text-gray-800 mb-6">Dados do Login</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Login SQL *
              </label>
              <input
                type="text"
                name="loginName"
                value={formData.loginName}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder="Nome do login no SQL Server"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usuário Web (opcional)
              </label>
              <select
                name="usuarioWebId"
                value={formData.usuarioWebId}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                disabled={loading}
              >
                <option value="">Nenhum</option>
                {usuarios.map(u => (
                  <option key={u.id} value={u.id}>{u.nome} ({u.email})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Horário Bloqueio Início
              </label>
              <input
                type="time"
                name="horarioBloqueioInicio"
                value={formData.horarioBloqueioInicio}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={loading || formData.removerBloqueioHorario}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Horário Bloqueio Fim
              </label>
              <input
                type="time"
                name="horarioBloqueioFim"
                value={formData.horarioBloqueioFim}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={loading || formData.removerBloqueioHorario}
              />
            </div>

            {/* Checkbox DEPOIS dos horários */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="removerBloqueioHorario"
                  checked={formData.removerBloqueioHorario}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700 font-medium">
                  {formData.removerBloqueioHorario 
                    ? "Login sem restrição de horário" 
                    : "Remover bloqueio de horário"}
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-6 mt-1">
                {formData.removerBloqueioHorario
                  ? "Desmarque para definir um horário de bloqueio"
                  : "Este login possui restrição de horário de acesso"}
              </p>
            </div>
          </div>
        </div>

        {/* Seção de Bancos - só aparece em edição */}
        {isEdit && (
          <div className="bg-white p-8 rounded-xl shadow border mb-6">
            <h2 className="text-lg font-medium text-gray-800 mb-6">
              Bancos com Acesso 
              <span className="text-sm font-normal text-gray-500 ml-2">({bancos.length} banco{bancos.length !== 1 ? 's' : ''})</span>
            </h2>
            
            {/* Lista de bancos */}
            {bancos.length > 0 ? (
              <div className="mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Banco</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Acesso</th>
                      <th className="text-center py-3 px-4 font-medium text-gray-600 w-20">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bancos.map(b => (
                      <tr key={b.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium text-slate-700">{b.banco}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            b.acesso === "A" ? "bg-red-100 text-red-700" :
                            b.acesso === "W" ? "bg-amber-100 text-amber-700" :
                            "bg-blue-100 text-blue-700"
                          }`}>
                            {b.acesso === "R" && "Leitura"}
                            {b.acesso === "W" && "Escrita"}
                            {b.acesso === "A" && "Admin"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoverBanco(b.id)}
                            className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                            title="Remover"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-gray-500 text-sm">Nenhum banco configurado para este login.</p>
              </div>
            )}

            {/* Adicionar novo banco - só mostra se houver bancos disponíveis */}
            {bancosDisponiveis.filter(b => !bancos.some(existing => existing.banco === b)).length > 0 && (
              <div className="flex gap-3 items-end border-t pt-6">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Adicionar Banco</label>
                  <select
                    value={novoBanco.banco}
                    onChange={(e) => setNovoBanco(prev => ({ ...prev, banco: e.target.value }))}
                    className="w-full p-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <option value="">Selecione um banco...</option>
                    {bancosDisponiveis
                      .filter(b => !bancos.some(existing => existing.banco === b))
                      .map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                  </select>
                </div>
                <div className="w-40">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Acesso</label>
                  <select
                    value={novoBanco.acesso}
                    onChange={(e) => setNovoBanco(prev => ({ ...prev, acesso: e.target.value }))}
                    className="w-full p-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <option value="R">Leitura</option>
                    <option value="W">Escrita</option>
                    <option value="A">Admin</option>
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (novoBanco.banco) {
                      handleAdicionarBanco(novoBanco.banco, novoBanco.acesso);
                      setNovoBanco({ banco: "", acesso: "R" });
                    }
                  }}
                  disabled={!novoBanco.banco}
                  className="px-4 py-2.5 bg-emerald-600 text-white rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Adicionar
                </button>
              </div>
            )}

            {/* Mensagem quando todos os bancos já foram adicionados */}
            {bancosDisponiveis.filter(b => !bancos.some(existing => existing.banco === b)).length === 0 && bancos.length > 0 && (
              <div className="border-t pt-6">
                <div className="p-4 bg-emerald-50 rounded-lg text-center">
                  <p className="text-emerald-700 text-sm font-medium">
                    ✓ Todos os bancos disponíveis já foram adicionados
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t pt-6">
          <button
            type="button"
            onClick={() => navigate("/controle-usuarios")}
            className="px-5 py-2 rounded-md border hover:bg-gray-50 disabled:opacity-50"
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar login"}
          </button>
        </div>
      </form>
    </div>
  );
}